import { useCallback, useEffect, useRef, useState } from "react";

export interface FeedPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface PagedFeed<T> {
  items: T[];
  /** True while the first page is loading. */
  loading: boolean;
  loadingMore: boolean;
  error: string | undefined;
  hasMore: boolean;
  loadMore: () => void;
}

/**
 * Cursor-paged list that accumulates pages.
 *
 * `useDashboardData` replaces its data on every fetch, which suits a screen
 * that shows one page at a time. The DM viewer appends instead ("Load more
 * threads", "Load earlier messages"), so it keeps its own list. Changing `deps`
 * (a different account or thread) drops the list and starts again from the
 * first page; `enabled: false` leaves it empty and fetches nothing.
 */
export const usePagedFeed = <T>(
  fetchPage: (cursor?: string) => Promise<FeedPage<T>>,
  deps: unknown[],
  enabled = true
): PagedFeed<T> => {
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Guards against a slow response for the previous account or thread landing
  // after the selection has already moved on.
  const requestId = useRef(0);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  useEffect(() => {
    const id = ++requestId.current;
    setItems([]);
    setNextCursor(null);
    setError(undefined);
    setLoadingMore(false);

    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchRef
      .current(undefined)
      .then(page => {
        if (id !== requestId.current) return;
        setItems(page.items);
        setNextCursor(page.nextCursor);
      })
      .catch((err: Error) => {
        if (id !== requestId.current) return;
        setError(err.message);
      })
      .finally(() => {
        if (id !== requestId.current) return;
        setLoading(false);
      });
    // `fetchPage` is an inline closure; the explicit deps list drives refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMore) return;
    const id = requestId.current;
    setLoadingMore(true);
    fetchRef
      .current(nextCursor)
      .then(page => {
        if (id !== requestId.current) return;
        setItems(previous => [...previous, ...page.items]);
        setNextCursor(page.nextCursor);
      })
      .catch((err: Error) => {
        if (id !== requestId.current) return;
        setError(err.message);
      })
      .finally(() => {
        if (id !== requestId.current) return;
        setLoadingMore(false);
      });
  }, [nextCursor, loadingMore]);

  return {
    items,
    loading,
    loadingMore,
    error,
    hasMore: nextCursor !== null,
    loadMore,
  };
};
